import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  FiDollarSign, FiDownload, FiPrinter, FiSearch, FiTrendingUp,
  FiTrendingDown, FiPercent, FiBarChart2, FiPackage
} from 'react-icons/fi';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getLocationsCached, getEmployeesCached, getJobsCached, getCategoriesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import ItemSelect from '../../components/ItemSelect';
import CustomerSelect from '../../components/CustomerSelect';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { addPrintBrand } from '../../utils/printBranding';

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3,
  '#4A90D9',
  '#E67E22',
  '#2ECC71',
  '#9B59B6'
];

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const ItemProfitLoss = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();

  // Data states
  const [transactions, setTransactions] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [locations, setLocations] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedSalesperson, setSelectedSalesperson] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedRegionId, setSelectedRegionId] = useState('all');
  const [selectedAreaId, setSelectedAreaId] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [includeReturns, setIncludeReturns] = useState(true);
  const [taxFilter, setTaxFilter] = useState('both');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortField, setSortField] = useState('profit');
  const [sortDirection, setSortDirection] = useState('desc');

  // Load master data
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(ref, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'inventoryItems');
      const [snapshot, cats] = await Promise.all([getDocs(ref), getCategoriesCached(activeCompany.id)]);
      // Items keep only the category's id — take its name from the category list.
      const catName = {};
      cats.forEach(c => { catName[c.id] = c.categoryName || ''; });
      setItems(snapshot.docs
        .map(d => { const it = { id: d.id, ...d.data() }; return { ...it, categoryName: catName[it.categoryId] || it.categoryName || '' }; })
        .filter(i => i.isDeleted !== true)
        .sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) {
      console.error('Error loading items:', error);
    }
  }, [activeCompany]);

  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'customers');
      const q = query(ref, orderBy('companyName', 'asc'));
      const snapshot = await getDocs(q);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.firstName != null)
        .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || ''))));
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.jobName != null)
        .sort((a, b) => String(a.jobName || '').localeCompare(String(b.jobName || ''))));
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  }, [activeCompany]);

  const loadRegions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'regions');
      const snapshot = await getDocs(ref);
      setRegions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  }, [activeCompany]);

  const loadAreas = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'areas');
      const snapshot = await getDocs(ref);
      setAreas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations([...rows]);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadItems();
    loadCustomers();
    loadEmployees();
    loadJobs();
    loadRegions();
    loadAreas();
    loadLocations();

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, [loadFinancialYears, loadItems, loadCustomers, loadEmployees, loadJobs, loadRegions, loadAreas, loadLocations]);

  // Item map for category lookup
  const itemMap = useMemo(() => {
    const map = {};
    items.forEach(i => { map[i.id] = i; });
    return map;
  }, [items]);

  // Customer map for region/area filtering
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  // Unique categories from items
  const categories = useMemo(() => {
    const cats = new Set();
    items.forEach(i => { if (i.categoryName) cats.add(i.categoryName); });
    return Array.from(cats).sort();
  }, [items]);

  // Cascading areas
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Load transactions
  const loadTransactions = async () => {
    if (!activeCompany?.id || !selectedFYId) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');
      const typeCodes = includeReturns ? ['SI', 'NSI', 'PS', 'SR', 'NSR'] : ['SI', 'NSI', 'PS'];
      const conditions = [
        where('tranTypeCode', 'in', typeCodes),
        where('financialYearId', '==', selectedFYId)
      ];

      const q = query(ref, ...conditions);
      const snapshot = await getDocs(q);
      let allTrans = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Only Posted/Paid/Partial
      allTrans = allTrans.filter(t => ['Posted', 'Paid', 'Partial'].includes(t.status));

      setTransactions(allTrans);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtered + aggregated item data
  const itemProfitData = useMemo(() => {
    let filtered = [...transactions];

    // Date range
    if (dateFrom) filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    if (dateTo) filtered = filtered.filter(t => (t.docDate || '') <= dateTo);

    // Customer
    if (selectedCustomer) filtered = filtered.filter(t => t.partyId === selectedCustomer);

    // Salesperson
    if (selectedSalesperson) filtered = filtered.filter(t => t.employeeId === selectedSalesperson);

    // Job
    if (selectedJob) filtered = filtered.filter(t => t.jobId === selectedJob);

    // Location
    if (selectedLocation) filtered = filtered.filter(t => t.locationId === selectedLocation);

    // Region/Area
    if (selectedRegionId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.regionId === selectedRegionId;
      });
    }
    if (selectedAreaId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.areaId === selectedAreaId;
      });
    }

    // Tax filter
    if (taxFilter === 'tax') {
      filtered = filtered.filter(t => !['NSI', 'NSR'].includes(t.tranTypeCode));
    } else if (taxFilter === 'nonTax') {
      filtered = filtered.filter(t => !['SI', 'SR'].includes(t.tranTypeCode));
    }

    // Aggregate by item
    const itemAgg = {};

    filtered.forEach(t => {
      const isReturn = ['SR', 'NSR'].includes(t.tranTypeCode);
      const lines = t.lines || [];

      lines.forEach(line => {
        if (!line.itemId) return;

        const key = line.itemId;
        if (!itemAgg[key]) {
          const masterItem = itemMap[key];
          itemAgg[key] = {
            itemId: key,
            itemCode: line.itemCode || masterItem?.itemCode || '',
            itemName: line.itemName || masterItem?.itemName || line.description || '',
            categoryName: masterItem?.categoryName || masterItem?.category || '-',
            unit: line.unitOfMeasure || masterItem?.unit || masterItem?.unitOfMeasure || 'Pcs',
            qtySold: 0,
            qtyReturned: 0,
            revenue: 0,
            returnRevenue: 0,
            cost: 0,
            returnCost: 0,
          };
        }

        const qty = Number(line.quantity) || 0;
        const lineTotal = Number(line.lineTotal) || 0;
        const lineCost = qty * (Number(line.unitCost) || 0);

        if (isReturn) {
          itemAgg[key].qtyReturned += qty;
          itemAgg[key].returnRevenue += lineTotal;
          itemAgg[key].returnCost += lineCost;
        } else {
          itemAgg[key].qtySold += qty;
          itemAgg[key].revenue += lineTotal;
          itemAgg[key].cost += lineCost;
        }
      });
    });

    // Calculate profit for each item
    return Object.values(itemAgg).map(item => {
      const netQty = item.qtySold - item.qtyReturned;
      const netRevenue = item.revenue - item.returnRevenue;
      const netCost = item.cost - item.returnCost;
      const profit = netRevenue - netCost;
      const margin = netRevenue !== 0 ? (profit / netRevenue) * 100 : 0;

      return { ...item, netQty, netRevenue, netCost, profit, margin };
    });
  }, [transactions, dateFrom, dateTo, selectedCustomer, selectedSalesperson, selectedJob, selectedLocation, selectedRegionId, selectedAreaId, customerMap, itemMap, taxFilter]);

  // Apply item/category filter + sort
  const filteredItemData = useMemo(() => {
    let data = [...itemProfitData];

    if (selectedItem) data = data.filter(d => d.itemId === selectedItem);
    if (selectedCategory) data = data.filter(d => d.categoryName === selectedCategory);

    data.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [itemProfitData, selectedItem, selectedCategory, sortField, sortDirection]);

  // Summary
  const summary = useMemo(() => {
    const totals = filteredItemData.reduce((acc, d) => {
      acc.revenue += d.netRevenue;
      acc.cost += d.netCost;
      acc.profit += d.profit;
      return acc;
    }, { revenue: 0, cost: 0, profit: 0 });

    totals.margin = totals.revenue !== 0 ? (totals.profit / totals.revenue) * 100 : 0;
    totals.itemCount = filteredItemData.length;
    return totals;
  }, [filteredItemData]);

  // Top 10 items by profit chart
  const profitChartData = useMemo(() => {
    return [...filteredItemData]
      .sort((a, b) => b.profit - a.profit)
      .slice(0, 10)
      .map(d => ({ name: d.itemName.length > 20 ? d.itemName.substring(0, 20) + '...' : d.itemName, profit: Math.round(d.profit) }));
  }, [filteredItemData]);

  // Margin distribution pie chart
  const marginDistData = useMemo(() => {
    const buckets = { 'Negative': 0, '0-10%': 0, '10-20%': 0, '20-30%': 0, '30%+': 0 };
    filteredItemData.forEach(d => {
      if (d.margin < 0) buckets['Negative']++;
      else if (d.margin < 10) buckets['0-10%']++;
      else if (d.margin < 20) buckets['10-20%']++;
      else if (d.margin < 30) buckets['20-30%']++;
      else buckets['30%+']++;
    });
    return Object.entries(buckets)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value }));
  }, [filteredItemData]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'itemName' || field === 'itemCode' ? 'asc' : 'desc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Export CSV
  const handleExport = () => {
    if (filteredItemData.length === 0) return;
    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Item-wise Profit & Loss Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    csv += `Total Revenue,${summary.revenue}\n`;
    csv += `Total Cost,${summary.cost}\n`;
    csv += `Total Profit,${summary.profit}\n`;
    csv += `Avg Margin,${summary.margin.toFixed(2)}%\n\n`;
    csv += 'Item Code,Item Name,Category,Unit,Qty Sold,Qty Returned,Net Qty,Revenue,Cost,Gross Profit,Margin %\n';

    filteredItemData.forEach(d => {
      csv += `"${d.itemCode}","${d.itemName}","${d.categoryName}","${d.unit}",${d.qtySold},${d.qtyReturned},${d.netQty},${d.netRevenue},${d.netCost},${d.profit},${d.margin.toFixed(2)}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Item_Profit_Loss_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Item-wise Profit & Loss</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        .negative{color:#DC3545}
        .positive{color:#28A745}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Item-wise Profit & Loss Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)} |
        Items: ${filteredItemData.length}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Total Revenue</div><div class="summary-val">${formatAmount(summary.revenue)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Cost</div><div class="summary-val">${formatAmount(summary.cost)}</div></div>
        <div class="summary-box"><div class="summary-label">Gross Profit</div><div class="summary-val ${summary.profit >= 0 ? 'positive' : 'negative'}">${formatAmount(summary.profit)}</div></div>
        <div class="summary-box"><div class="summary-label">Avg Margin</div><div class="summary-val">${summary.margin.toFixed(2)}%</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Code</th><th>Item Name</th><th>Category</th><th class="right">Qty Sold</th>
          <th class="right">Qty Ret.</th><th class="right">Net Qty</th>
          <th class="right">Revenue</th><th class="right">Cost</th>
          <th class="right">Profit</th><th class="right">Margin %</th>
        </tr></thead>
        <tbody>
          ${filteredItemData.map(d => `
            <tr>
              <td>${d.itemCode || '-'}</td>
              <td>${d.itemName}</td>
              <td>${d.categoryName}</td>
              <td class="right">${d.qtySold}</td>
              <td class="right">${d.qtyReturned}</td>
              <td class="right">${d.netQty}</td>
              <td class="right">${formatAmount(d.netRevenue)}</td>
              <td class="right">${formatAmount(d.netCost)}</td>
              <td class="right ${d.profit >= 0 ? 'positive' : 'negative'}">${formatAmount(d.profit)}</td>
              <td class="right ${d.margin >= 0 ? 'positive' : 'negative'}">${d.margin.toFixed(2)}%</td>
            </tr>
          `).join('')}
          <tr class="totals-row">
            <td colspan="6" style="text-align:right">Totals (${filteredItemData.length} items)</td>
            <td class="right">${formatAmount(summary.revenue)}</td>
            <td class="right">${formatAmount(summary.cost)}</td>
            <td class="right ${summary.profit >= 0 ? 'positive' : 'negative'}">${formatAmount(summary.profit)}</td>
            <td class="right">${summary.margin.toFixed(2)}%</td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Chart tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{payload[0].name}</ChartTooltipLabel>
          <ChartTooltipRow>
            <strong>{payload[0].value} item(s)</strong>
          </ChartTooltipRow>
        </ChartTooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Item-wise Profit & Loss" breadcrumb="Reports / Sale Reports / Item-wise Profit & Loss">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={selectedCustomer}
              onChange={setSelectedCustomer}
              allOption={{ value: '', label: 'All Customers' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Item</Label>
            <ItemSelect
              items={items}
              selectedItemId={selectedItem}
              onChange={setSelectedItem}
              allOption={{ value: '', label: 'All Items' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Category</Label>
            <Select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
              <option value="">All Categories</option>
              {categories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Salesperson</Label>
            <Select value={selectedSalesperson} onChange={e => setSelectedSalesperson(e.target.value)}>
              <option value="">All Salespersons</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.employeeCode ? `${emp.employeeCode} - ` : ''}{emp.fullName || emp.firstName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Job</Label>
            <Select value={selectedJob} onChange={e => setSelectedJob(e.target.value)}>
              <option value="">All Jobs</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>
                  {j.jobCode ? `${j.jobCode} - ` : ''}{j.jobName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Location</Label>
            <Select value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)}>
              <option value="">All Locations</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.locationName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Include Returns</Label>
            <Select value={includeReturns ? 'yes' : 'no'} onChange={e => setIncludeReturns(e.target.value === 'yes')}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={filteredItemData.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={filteredItemData.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadTransactions} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiTrendingUp /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Revenue</SummaryLabel>
              <SummaryValue style={{ color: '#28A745' }}>{formatCurrency(summary.revenue)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#DC3545"><FiTrendingDown /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Cost</SummaryLabel>
              <SummaryValue style={{ color: '#DC3545' }}>{formatCurrency(summary.cost)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Gross Profit</SummaryLabel>
              <SummaryValue style={{ color: summary.profit >= 0 ? '#28A745' : '#DC3545' }}>
                {formatCurrency(summary.profit)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent3}><FiPercent /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Avg Margin</SummaryLabel>
              <SummaryValue>{summary.margin.toFixed(2)}%</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent1}><FiPackage /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Items</SummaryLabel>
              <SummaryValue>{summary.itemCount}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiBarChart2 /></EmptyIcon>
              <EmptyText>Loading item profit data...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiBarChart2 /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view item-wise profit & loss</EmptyText>
            </EmptyState>
          ) : filteredItemData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiBarChart2 /></EmptyIcon>
              <EmptyText>No item data found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th $sortable onClick={() => handleSort('itemCode')}>Code{getSortIndicator('itemCode')}</Th>
                  <Th $sortable onClick={() => handleSort('itemName')}>Item Name{getSortIndicator('itemName')}</Th>
                  <Th $sortable onClick={() => handleSort('categoryName')}>Category{getSortIndicator('categoryName')}</Th>
                  <Th $sortable $right onClick={() => handleSort('qtySold')}>Qty Sold{getSortIndicator('qtySold')}</Th>
                  <Th $sortable $right onClick={() => handleSort('qtyReturned')}>Qty Ret.{getSortIndicator('qtyReturned')}</Th>
                  <Th $sortable $right onClick={() => handleSort('netQty')}>Net Qty{getSortIndicator('netQty')}</Th>
                  <Th $sortable $right onClick={() => handleSort('netRevenue')}>Revenue{getSortIndicator('netRevenue')}</Th>
                  <Th $sortable $right onClick={() => handleSort('netCost')}>Cost{getSortIndicator('netCost')}</Th>
                  <Th $sortable $right onClick={() => handleSort('profit')}>Profit{getSortIndicator('profit')}</Th>
                  <Th $sortable $right onClick={() => handleSort('margin')}>Margin %{getSortIndicator('margin')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredItemData.map((d, index) => (
                  <Tr key={d.itemId || index}>
                    <Td>{d.itemCode || '-'}</Td>
                    <Td style={{ fontWeight: 500 }}>{d.itemName}</Td>
                    <Td>{d.categoryName}</Td>
                    <AmountCell $right>{d.qtySold}</AmountCell>
                    <AmountCell $right $type={d.qtyReturned > 0 ? 'return' : undefined}>{d.qtyReturned}</AmountCell>
                    <AmountCell $right>{d.netQty}</AmountCell>
                    <AmountCell $right>{formatAmount(d.netRevenue)}</AmountCell>
                    <AmountCell $right>{formatAmount(d.netCost)}</AmountCell>
                    <AmountCell $right $type={d.profit >= 0 ? 'sale' : 'return'}>
                      {formatAmount(d.profit)}
                    </AmountCell>
                    <MarginCell $value={d.margin}>{d.margin.toFixed(2)}%</MarginCell>
                  </Tr>
                ))}

                <TotalsRow>
                  <Td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({filteredItemData.length} items)
                  </Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(summary.revenue)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(summary.cost)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700, color: summary.profit >= 0 ? '#28A745' : '#DC3545' }}>
                    {formatAmount(summary.profit)}
                  </AmountCell>
                  <MarginCell $value={summary.margin} style={{ fontWeight: 700 }}>{summary.margin.toFixed(2)}%</MarginCell>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasSearched && !loading && filteredItemData.length > 0 && (
        <ChartsRow>
          <ChartSection>
            <ChartTitle>Top 10 Items by Profit</ChartTitle>
            {profitChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={profitChartData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke={colors.text.secondary} width={140} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="profit" name="Gross Profit" fill={colors.chart.primary} radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No data available</ChartEmpty>
            )}
          </ChartSection>

          <ChartSection>
            <ChartTitle>Margin Distribution</ChartTitle>
            {marginDistData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={marginDistData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {marginDistData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  color: ${props => {
    if (props.$type === 'return') return '#DC3545';
    if (props.$type === 'sale') return '#28A745';
    return colors.text.primary;
  }};
`;

const MarginCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  font-weight: 500;
  color: ${props => {
    const v = props.$value || 0;
    if (v >= 20) return '#28A745';
    if (v >= 10) return '#F57C00';
    if (v >= 0) return '#FF9800';
    return '#DC3545';
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default ItemProfitLoss;
